import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input, Row, Col, Typography, Checkbox, notification, Space, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import AgGridTable from '../common/AgGridTable';
import moment from 'moment';
import { connect } from 'react-redux';
import { fetchMaterialLog, updateMaterialLog, updateDsrLogSections } from '../../redux/dsr/action';
import get from 'lodash/get';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MaterialConsumptionSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSubmitted,
  onComplete,
  onCancel,
  onFetchMaterialLog,
  onUpdateMaterialLog,
  isMaterialLogLoading,
  isUpdatingMaterialLog,
  currentMaterialLog,
  onUpdateDsrLogSections,
  isUpdatingDsrLogSections,
  isSkipped,
  isShiftSkipped = false,
  onSkipChange
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const displayDate = selectedDate ? moment(selectedDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY');
  const shiftKey = shift === 'day' ? 'isMaterialUsageSkippedDayShift' : 'isMaterialUsageSkippedNightShift';

  const fetchLogData = useCallback(() => {
    if (selectedProject && selectedDate && currentDsrLog) {
      const user = JSON.parse(localStorage.getItem('user'));
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftValue = shift === 'day' ? '0' : '1';

          onFetchMaterialLog({
            projectId: selectedProject.id,
            logDate: logDate,
            userEmail: user.email,
            shift: shiftValue,
              sowId: selectedMainline?.id
            });
      }
    }, [selectedProject, selectedDate, currentDsrLog, shift, selectedMainline, onFetchMaterialLog]);

  useEffect(() => {
    fetchLogData();
  }, [fetchLogData]);

  // Update skip state from props
  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  // Reset dirty state when date changes (new data should be loaded)
  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  // Reset skip state when section or shift changes
  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  // Clear local state when component unmounts
  useEffect(() => {
    return () => {
      setRowData([]);
      setComments('');
      setSkipped(false);
      setIsDirty(false);
      setGridApi(null);
    };
  }, []);

  useEffect(() => {
    if (currentMaterialLog && Array.isArray(currentMaterialLog)) {
          const formattedData = currentMaterialLog.map(item => ({
            ...item,
              totalRemaining: (item.pendingQuantityInStock || 0) - (parseFloat(item.totalUsedToday) || 0)
            }));
          setRowData(formattedData);

          if (currentMaterialLog.length > 0) {
            const shiftValue = shift === 'day' ? '0' : '1';
          setComments(shiftValue === '0' ?
            (currentMaterialLog[0].dlmDayShiftComments || '') :
            (currentMaterialLog[0].dlmNightShiftComments || '')
          );
        }
      }
    }, [currentMaterialLog, shift]);

  useEffect(() => {
    if (gridApi) {
      if (isMaterialLogLoading) {
        gridApi.showLoadingOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  }, [isMaterialLogLoading, gridApi]);

  const columnDefs = [
    { headerName: 'Material Category', field: 'materialCategory', editable: false },
    { headerName: 'Name', field: 'materialName', editable: false },
    { headerName: 'BatchNumber', field: 'batchNumber', editable: false },
    {
      headerName: 'Expiry Date',
      field: 'expiryDate',
      editable: false,
        valueFormatter: params => params.value ? moment(params.value).format('YYYY-MM-DD') : ''
      },
      { headerName: 'Product Code', field: 'productCode', editable: false },
      { headerName: 'Unit', field: 'unit', editable: false },
      { headerName: 'Total Stock Start of Shift', field: 'pendingQuantityInStock', editable: false, type: 'numericColumn' },
      {
        headerName: 'Total Used In Shift',
        field: 'totalUsedToday',
        editable: (params) => {
          const stock = params.data?.pendingQuantityInStock ?? 0;
          return !skipped && !isSubmitted && Number(stock) > 0;
        },
        type: 'numericColumn',
        suppressClickEdit: false,
          cellStyle: params => {
            const stock = params.data?.pendingQuantityInStock ?? 0;
            const canEdit = !skipped && !isSubmitted && Number(stock) > 0;
            if (canEdit) {
              return {
                backgroundColor: 'rgb(50, 29, 30)',
                borderRadius: '8px',
                border: '1px solid #666',
                transition: 'all 0.3s ease'
              };
            }
            return null;
          },
        cellClass: params => {
          const stock = params.data?.pendingQuantityInStock ?? 0;
          const canEdit = !skipped && !isSubmitted && Number(stock) > 0;
          if (canEdit) return 'editable-cell-highlight';
          return null;
        }
      },
      { headerName: 'Total Remaining', field: 'totalRemaining', editable: false, type: 'numericColumn' },
    ];

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    params.api.autoSizeAllColumns();
  }, []);

  const onCellValueChanged = useCallback((params) => {
      const { data, field, newValue } = params;

      if (field === 'totalUsedToday') {
        const pendingStock = Number(data.pendingQuantityInStock ?? 0);

        // Do not allow used qty when material stock is zero (same as mobile app restriction)
        if (pendingStock <= 0) {
          notification.warning({
            message: 'Cannot enter used quantity',
            description: 'Used quantity in shift is not allowed when material stock is zero.',
            placement: 'bottomRight',
          });
          params.node.setDataValue(field, 0);
          return;
        }

        const usedValue = parseFloat(newValue) || 0;
        if (usedValue > pendingStock) {
          notification.warning({
            message: 'Invalid Quantity',
            description: `Used quantity (${usedValue}) cannot exceed pending stock (${pendingStock}). Value will be reset to 0.`,
            placement: 'bottomRight',
          });
          params.node.setDataValue(field, 0);
          return;
        }
      }

      const newRowData = rowData.map(row => {
        if (row.rowKey === data.rowKey) {
          const updatedRow = { ...row, [field]: newValue };
          updatedRow.totalRemaining = (updatedRow.pendingQuantityInStock || 0) - (parseFloat(updatedRow.totalUsedToday) || 0);
          return updatedRow;
        }
        return row;
      });
      setRowData(newRowData);
      setIsDirty(true);
    }, [rowData]);

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

      // Use the parent component's centralized skip handler
      if (onSkipChange) {
        onSkipChange(e);
      } else {
        // Fallback to local handling if no parent handler provided
        if (currentDsrLog?.id) {
          const submissionStatus = generateSubmissionStatus('material', shift, true);
          const sectionsSkipped = {
            ...currentDsrLog.sectionsSkipped,
            isMaterialUsageSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedDayShift || 0),
            isMaterialUsageSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedNightShift || 0),
          };

          onUpdateDsrLogSections({
            dsrLogId: currentDsrLog.id,
            submissionStatus: submissionStatus,
            sectionsSkipped: sectionsSkipped
          });
        }
      }
    };

  // Helper function to generate submission status fields for a specific section and shift
  const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
    const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

      switch (section) {
        case 'technician':
          submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
          break;
        case 'downtime':
          submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
          break;
        case 'consumables':
          submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
          break;
        case 'equipment':
          submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
          break;
        case 'ehs':
          submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
          break;
        case 'material':
          submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
          break;
        case 'production':
          submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
          break;
        default:
          return {};
      }

      return { [submittedKey]: isSubmitted };
    };

  const handleSave = async () => {
    if (skipped) {
      // If skipped, just complete without saving data to API
      if (onComplete) onComplete(true, 'material');
      return;
    }

      const dsrLogId = rowData.length > 0 ? rowData[0].dsrLogId : currentDsrLog?.id;

      if (!dsrLogId) {
        notification.error({ message: 'Cannot determine DSR Log ID to save data.' });
        return;
      }

      try {
          const shiftValue = shift === 'day' ? '0' : '1';
          const dsrLogMaterial = rowData.map(item => ({
            projectMaterialId: item.projectMaterialId,
            dsrLogId: dsrLogId,
            totalUsedToday: item.totalUsedToday || "0",
            shift: shiftValue
          }));

          const payload = {
              dsrLogMaterial,
              comments: {
                dsrLogId: dsrLogId,
                dlmDayShiftComments: shiftValue === '0' ? comments : (currentMaterialLog[0]?.dlmDayShiftComments || ''),
                dlmNightShiftComments: shiftValue === '1' ? comments : (currentMaterialLog[0]?.dlmNightShiftComments || '')
              }
            };

          await onUpdateMaterialLog(payload);
          setIsDirty(false);

          // Update submission status and sectionsSkipped for this section
          const submissionStatus = generateSubmissionStatus('material', shift, true);
          const sectionsSkipped = {
            ...currentDsrLog.sectionsSkipped,
            isMaterialUsageSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedDayShift || 0),
            isMaterialUsageSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedNightShift || 0)
          };

          await onUpdateDsrLogSections({
              dsrLogId: dsrLogId,
              submissionStatus: submissionStatus,
              sectionsSkipped: sectionsSkipped
            });

          // Only navigate to next section on successful save
          if (onComplete) onComplete(true, 'material');

          // Refresh data after successful save
          if (selectedProject && selectedMainline && selectedDate) {
            const userEmail = JSON.parse(localStorage.getItem('user')).email;
            const logDate = moment(selectedDate).format('YYYY-MM-DD');
            const shiftNumber = shift === 'day' ? '0' : '1';

              onFetchMaterialLog({
                projectId: selectedProject.id,
                logDate: logDate,
                userEmail: userEmail,
                shift: shiftNumber,
                sowId: selectedMainline.id
              });
            }

      } catch (error) {
        console.error('Error saving material consumption section:', error);
        notification.error({
          message: 'Save Failed',
          description: 'Failed to save material consumption data. Please try again.'
        });
        // Stay in the same section on error - button will be re-enabled
      }
    };

  return (
    <div style={{ padding: 24, position: 'relative' }}>
      <Spin
        spinning={isMaterialLogLoading}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        tip="Loading material consumption data..."
        size="large"
      >
        {isShiftSkipped ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: 'rgba(250, 173, 20, 0.1)',
              border: '1px solid #faad14',
              borderRadius: 8,
              marginBottom: 24
            }}>
              <div style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#faad14',
                marginBottom: 8
              }}>
                {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
              </div>
              <div style={{
                fontSize: 14,
                color: '#666'
              }}>
                This section is disabled because the {shift} shift has been skipped.
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, height: '40vh', overflow: 'auto' }}>
                <AgGridTable
                  rowData={rowData}
                  columnDefs={columnDefs}
                  onCellValueChanged={onCellValueChanged}
                  onGridReady={onGridReady}
                  theme="ag-theme-material-dark"
                />
              </div>

              <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col span={24}>
                  <Title level={5} style={{ textAlign: 'left' }}>Comments</Title>
                  <TextArea
                    rows={3}
                    placeholder="Add any comments here..."
                    value={comments}
                    onChange={(e) => { setComments(e.target.value); setIsDirty(true); }}
                    disabled={skipped || isSubmitted}
                  />
                </Col>
              </Row>
            </>
          )}
        </Spin>

        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 12 }}>
          <Button onClick={onCancel} size="large" shape="round">Cancel</Button>
          <Button
            onClick={handleSave}
            type="primary"
            size="large"
            shape="round"
            loading={isUpdatingMaterialLog || isUpdatingDsrLogSections}
            disabled={isUpdatingMaterialLog || isUpdatingDsrLogSections || isSubmitted || isShiftSkipped}
          >
            Save & Next
          </Button>
        </div>
      </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isMaterialLogLoading: get(dsrReducer, 'isMaterialLogLoading', false),
  isUpdatingMaterialLog: get(dsrReducer, 'isUpdatingMaterialLog', false),
  currentMaterialLog: get(dsrReducer, 'currentMaterialLog', null),
  isUpdatingDsrLogSections: get(dsrReducer, 'isUpdatingDsrLogSections', false),
});

const mapDispatchToProps = {
  onFetchMaterialLog: fetchMaterialLog,
  onUpdateMaterialLog: updateMaterialLog,
  onUpdateDsrLogSections: updateDsrLogSections,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialConsumptionSection); 